import { Box, capitalize, Chip, Typography } from '@mui/material';
import { isNull } from 'lodash';
import React, { FC, ReactNode } from 'react';
import {
  ArticleLikeInput,
  BrandKey,
  CategoryStatus,
  GetArticleQuery_query_article_Article_guides_ArticleGuide_category_Category,
  GetArticleQuery_query_article_Article_tags_ArticleTag_tag_Tag,
  GetSuggestedTagsListQuery_query_suggestTags_Tag,
  GetSuggestedTagsListQuery_query_suggestTags_Tag_brandedContents_TagBrandedContent,
} from '../../../__generated__/queries-web';
import { isEventTag } from '../../../types/guards';
import { pluralizeText } from '../../../utils/pluralizeText';
import { TAGS_AUTOCOMPLETE_LIST_HEADER } from '../constants';

// MEMOIZED SHARED COMPONENTS
interface AutocompleteListHeaderProps {
  label?: string;
  listLength: number;
  secondaryLabel?: ReactNode | string;
  showListLength?: boolean;
}
export const AutocompleteListHeader: FC<AutocompleteListHeaderProps> = React.memo(
  ({ label, listLength, secondaryLabel, showListLength = true }) => (
    <span style={{ alignItems: 'center', display: 'flex' }}>
      {label || TAGS_AUTOCOMPLETE_LIST_HEADER}

      {showListLength && (
        <Typography
          variant="body2"
          color="primary"
          style={{ marginLeft: '4px' }}
        >
          ({listLength})
        </Typography>
      )}

      {secondaryLabel && (
        <span style={{ marginLeft: 'auto' }}>{secondaryLabel}</span>
      )}
    </span>
  ),
);

// SHARED FUNCTIONS
// export const getProvidersByBrandKey = (brandKey: BrandKey): TagType[] =>
//   brandsOptions[brandKey].tagProviders || [];

export const getSuggestedTagsArticleParams = (article?: ArticleLikeInput) => ({
  title: article?.title || undefined,
  lead: article?.lead || undefined,
  body: article?.body || undefined,
});

interface TagsAutoCompleteListOpts {
  withActivationControl?: boolean;
  withArticleCountSort?: boolean;
  withSecondaryLabel?: boolean;
}

export const isTagInactive = (
  tag: GetSuggestedTagsListQuery_query_suggestTags_Tag,
  brandKey: BrandKey,
): boolean => {
  if (tag.type !== 'Tag') {
    return false;
  }

  const brandedContent = tag.brandedContents?.find(
    (
      content: GetSuggestedTagsListQuery_query_suggestTags_Tag_brandedContents_TagBrandedContent,
    ) => content.brandKey === brandKey,
  );

  if (brandedContent) {
    return brandedContent.isActive === false;
  }

  return false;
};

export const getTagsAutoCompleteList = (
  list: GetSuggestedTagsListQuery_query_suggestTags_Tag[],
  selectedList: (
    | GetArticleQuery_query_article_Article_tags_ArticleTag_tag_Tag
    | GetArticleQuery_query_article_Article_guides_ArticleGuide_category_Category
  )[],
  brandKey: BrandKey,
  opts: TagsAutoCompleteListOpts = {},
) => {
  return list.map((tag) => {
    const isAlreadySelected =
      selectedList.findIndex(
        (item) =>
          (!isNull(item.id) && item.id === tag.id) || item.title === tag.title,
      ) > -1;

    const articleCount = tag.articleUsages?.find(
      (usage) => usage.brandKey === brandKey,
    )?.total;

    const secondaryLabel = getSecondaryLabel(
      tag,
      articleCount || 0,
      opts,
      isTagInactive(tag, brandKey),
    );

    return {
      additionnals: tag,
      selected: isAlreadySelected,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tag.id!,
      label: capitalize(tag.title),
      isInactive: isTagInactive(tag, brandKey),
      secondaryLabel,
    };
  });
};

const getSecondaryLabel = (
  { type, status }: any,
  articleCount: number,
  opts: TagsAutoCompleteListOpts,
  isInactive: boolean,
): string | ReactNode => (
  <div
    style={{
      alignItems: 'center',
      display: 'flex',
    }}
  >
    {opts?.withSecondaryLabel ? (
      isEventTag({ type }) ? (
        status === CategoryStatus.Draft ? (
          <Chip
            size="small"
            label="Brouillon"
            variant="outlined"
            style={{ marginLeft: '5px' }}
          />
        ) : null
      ) : (
        <Box
          sx={
            // We must check the strict equality because a tag can be active with isEnabled === null
            type === 'Tag' && isInactive
              ? {
                  fontStyle: 'italic',
                  color: 'gray',
                }
              : {}
          }
        >
          {type} - {pluralizeText(articleCount || 0, 'article', true)}
        </Box>
      )
    ) : null}
  </div>
);
